import React, { SelectHTMLAttributes } from 'react';
import classnames from 'classnames';
import styled from 'styled-components';

import PlayupThemeProvider from '../../themes/PlayupThemeProvider';
import {
	getPrimaryTextColor,
	getSecondaryTextColor,
	getTextInputBackgroundColor,
	getTextInputBorderColor,
} from '../../themes/themeSelector';
import useScreenQuery from '../../hooks/useScreenQuery';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

const SelectInput = React.forwardRef<HTMLSelectElement, Props>(
	({ kind = 'blackslate', className, children, error, label, editable = true, ...rest }, ref) => {
		const { isSmall } = useScreenQuery();

		return (
			<PlayupThemeProvider>
				<div className={className}>
					{label && (
						<Label $isSmallScreen={isSmall} kind={kind} htmlFor={rest.name}>
							{label}
						</Label>
					)}
					<Select
						$editable={editable}
						$isSmallScreen={isSmall}
						kind={kind}
						className={classnames(error && 'error')}
						ref={ref}
						{...rest}
					>
						<>
							{rest.placeholder && <option value="">{rest.placeholder}</option>}
							{children}
						</>
					</Select>
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</div>
			</PlayupThemeProvider>
		);
	}
);

export default SelectInput;

type Props = {
	kind?: string;
	className?: string;
	error?: string;
	label?: string;
	editable?: boolean;
} & SelectHTMLAttributes<HTMLSelectElement>;

const Label = styled.label<{ kind: string; $isSmallScreen: boolean }>`
	font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? '14px' : '16px')};
	margin-bottom: ${({ $isSmallScreen }) => ($isSmallScreen ? '5px' : '7px')};
	display: block;
	color: ${({ theme, kind }) => getSecondaryTextColor(theme, kind)};
	user-select: none;
`;

const Select = styled.select<{ kind: string; $isSmallScreen: boolean; $editable?: boolean }>`
	width: 100%;
	padding: ${({ $isSmallScreen }) => ($isSmallScreen ? '10px' : '10px 18px')};
	border-radius: ${({ $isSmallScreen }) => ($isSmallScreen ? '2px' : '4px')};
	border: 2px solid ${({ theme, kind }) => getTextInputBorderColor(theme, kind)};
	font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? '14px' : '18px')};
	line-height: ${({ $isSmallScreen }) => ($isSmallScreen ? '16px' : '26px')};
	color: ${({ theme, kind, $editable }) => ($editable ? getPrimaryTextColor(theme, kind) : theme.colors.greyR)};
	background-color: ${({ theme, kind }) => getTextInputBackgroundColor(theme, kind)};

	appearance: none;
	-moz-appearance: none;
	-webkit-appearance: none;

	&.error {
		border-color: ${({ theme }) => theme.colors.danger};
	}

	&:focus {
		outline-color: ${({ theme, kind }) => getPrimaryTextColor(theme, kind)};
	}

	::placeholder {
		color: ${({ theme, kind }) => getSecondaryTextColor(theme, kind)};
	}
`;
